// The WebGPU backend: the primitives of `gfx/types.ts`, built over the
// context of this folder.
//
// It is the twin of `gl/backend.ts` and it drops into the same slot: the
// engine asks for one of these and then speaks to interfaces only.
//
// The device is already live when this runs — `gfx/select.ts` asked for it
// before the canvas was touched — so the whole of what is left is the canvas
// claim inside `createGpuContext`, and that claim is the LAST thing the
// bring-up does.

import { createGpuContext } from './context';
import { GpuPipeline } from './pipeline';
import { GpuInstanceBuffer } from './instances';
import { GpuMeshBuffer } from './mesh';
import { GpuDataTexture } from './data-texture';
import { GpuAtlasTexture } from './atlas-texture';
import type { GpuBundle } from './device';
import type { QueryKnobs } from '../knobs';
import {
  FALLBACK_MAX_TEXTURE,
  type AttribSpec, type DataFormat, type GfxBackend,
} from '../gfx/types';
import type { ProgramSource } from '../shaders/program';

/**
 * Claims the canvas for WebGPU and hands back the primitives.
 *
 * It THROWS where the canvas gives no context or the configure fails. The
 * caller does not fall back to WebGL2 then: the canvas is already spent, and
 * a device that answered the probe and then refused the canvas is a fault
 * worth a stop.
 */
export function createGpuBackend(
  canvas: HTMLCanvasElement, bundle: GpuBundle, knobs?: QueryKnobs,
): GfxBackend {
  const gpu = createGpuContext(canvas, bundle, knobs);
  return {
    gfx: gpu,
    pipeline(src: ProgramSource) {
      return new GpuPipeline(gpu, src);
    },
    instances(floatsPerInstance: number, attribs: readonly AttribSpec[], capacity: number) {
      return new GpuInstanceBuffer(gpu, floatsPerInstance, attribs, capacity);
    },
    mesh(floatsPerVertex: number, attribs: readonly AttribSpec[]) {
      return new GpuMeshBuffer(gpu, floatsPerVertex, attribs);
    },
    dataTexture(
      name: string, width: number, height: number, format: DataFormat, linear?: boolean,
    ) {
      return new GpuDataTexture(gpu, name, width, height, format, linear);
    },
    atlasTexture(source: HTMLCanvasElement) {
      return new GpuAtlasTexture(gpu, source);
    },
    maxTextureSize() {
      return Number(gpu.device.limits?.maxTextureDimension2D) || FALLBACK_MAX_TEXTURE;
    },
  };
}
